/**
 * Inspelningen i webbläsaren. Renderas ALLTID med `key={moteId}` (se
 * useInspelning): kön och delräknaren hör till ett enda möte.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useInspelning } from '@/hooks/useInspelning';
import { antal, klockText } from './motesText';

interface Props {
  moteId: string;
  /** Inspelningen är sparad och mötet har gått vidare till utskrift. */
  onKlar: () => void;
  /** Mötet ska tas bort (inspelningen kom aldrig igång). */
  onTaBort: () => void;
  tarBort: boolean;
  /** Pågår en inspelning (eller väntar delar på uppladdning)? Sidan döljer då möteslistan. */
  onAktiv?: (aktiv: boolean) => void;
}

const LAMNA_TEXT = 'Inspelningen pågår. Lämnar du sidan stoppas den – det som redan laddats upp sparas. Lämna ändå?';

export function Inspelare({ moteId, onKlar, onTaBort, tarBort, onAktiv }: Props) {
  const ins = useInspelning(moteId);
  const startad = useRef(false);
  const [avslutar, setAvslutar] = useState(false);
  const { starta, status } = ins;

  // Startar direkt: användaren har redan tryckt "Starta inspelning".
  useEffect(() => {
    if (startad.current) return;
    startad.current = true;
    void starta();
  }, [starta]);

  useEffect(() => {
    if (status === 'klar') onKlar();
  }, [status, onKlar]);

  async function avsluta() {
    setAvslutar(true);
    try {
      await ins.avsluta();
    } finally {
      setAvslutar(false);
    }
  }

  const spelar = ins.status === 'spelar_in';
  const pausad = ins.status === 'pausad';
  const sparar = ins.status === 'laddar_upp';
  const aktiv = spelar || pausad || ins.vantandeDelar > 0 || avslutar;

  useEffect(() => {
    onAktiv?.(aktiv);
  }, [aktiv, onAktiv]);
  useEffect(() => () => onAktiv?.(false), [onAktiv]);

  // En länk inne i appen (menyn, en listrad) avmonterar inspelningen. Fråga först.
  // Capture på document körs före React Routers klickhanterare, som respekterar defaultPrevented.
  useEffect(() => {
    if (!aktiv) return;
    const klick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      if (!window.confirm(LAMNA_TEXT)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('click', klick, true);
    return () => document.removeEventListener('click', klick, true);
  }, [aktiv]);
  // Inget ljud alls kom in (även när klockan gick men inspelaren aldrig gav en del):
  // då kan mötet bara tas bort eller startas om – "Avsluta" skulle ge samma fel igen.
  const aldrigIgang = ins.status === 'fel' && ins.inspeladeDelar === 0 && ins.vantandeDelar === 0;

  let lage = 'Startar mikrofonen…';
  if (spelar) lage = 'Spelar in';
  else if (pausad) lage = 'Pausad';
  else if (sparar) lage = avslutar ? 'Sparar inspelningen…' : 'Inspelningen har stoppats';
  else if (ins.status === 'klar') lage = 'Sparad';
  else if (ins.status === 'fel') lage = 'Inspelningen stannade';

  return (
    <div className="rounded-xl border border-ink-200 bg-surface-sunken p-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
        <div className="flex items-center gap-3 min-w-0">
          {/* Rött betyder här "mikrofonen är på". */}
          <span
            className={cn('relative inline-flex w-3 h-3 rounded-full shrink-0', spelar ? 'bg-red-600' : 'bg-ink-300')}
            aria-hidden
          >
            {spelar && (
              <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-60 motion-reduce:hidden" />
            )}
          </span>
          <div className="min-w-0">
            <p
              className={cn('text-[13px] font-semibold', spelar ? 'text-red-700' : 'text-ink-700')}
              role="status"
              aria-live="polite"
            >
              {lage}
            </p>
            <p
              className="text-[30px] leading-none font-semibold tabular-nums text-ink-900 mt-1"
              aria-label={`Inspelad tid ${klockText(ins.sekunder)}`}
            >
              {klockText(ins.sekunder)}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          {spelar && (
            <Button variant="secondary" onClick={ins.pausa}>
              Pausa
            </Button>
          )}
          {pausad && (
            <Button variant="secondary" onClick={ins.fortsatt}>
              Fortsätt
            </Button>
          )}
          {(spelar || pausad || sparar || (ins.status === 'fel' && !aldrigIgang)) && (
            <Button onClick={() => void avsluta()} loading={avslutar}>
              Avsluta och skriv ut
            </Button>
          )}
          {aldrigIgang && (
            <>
              <Button variant="secondary" onClick={() => void ins.starta()}>
                Försök igen
              </Button>
              <Button variant="ghost" onClick={onTaBort} loading={tarBort}>
                Ta bort mötet
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-1.5 text-[12.5px] text-ink-600 leading-relaxed">
        {ins.vantandeDelar > 0 && <p>{antal(ins.vantandeDelar, 'del', 'delar')} väntar på att laddas upp.</p>}
        {ins.meddelande && (
          <p
            className={cn(ins.status === 'fel' && 'text-red-700 font-medium')}
            role={ins.status === 'fel' ? 'alert' : undefined}
          >
            {ins.meddelande}
          </p>
        )}
        {aktiv && <p className="font-semibold text-ink-900">Lämna inte sidan medan du spelar in.</p>}
        {(spelar || pausad) && (
          <p>Inspelningen laddas upp en minut i taget, så inget går förlorat om nätet försvinner en stund.</p>
        )}
      </div>
    </div>
  );
}
